// src/pages/TermsOfServicePage.js
import React from 'react';
import { Link } from 'react-router-dom';
import './PolicyPage.css';

const LAST_UPDATED = 'March 12, 2026';
const CONTACT_EMAIL = 'piervaconsulting@gmail.com';

function TermsOfServicePage() {
  return (
    <div className="policy-page">
      <div className="policy-container">
        <div className="policy-header">
          <Link to="/" className="policy-back">← Back to Login</Link>
          <img src="/favicon.svg" alt="quickLOG" className="policy-logo" />
          <h1>Terms of Service</h1>
          <p className="policy-meta">Last updated: {LAST_UPDATED}</p>
        </div>

        <div className="policy-body">

          <p className="policy-intro">
            These Terms of Service ("Terms") govern your organization's access to and use of
            the quickLOG platform ("Service") provided by PV Business Consulting, LLC
            ("Company", "we", "us", or "our"). By accessing or using the Service, the
            organization ("Client", "you") agrees to be bound by these Terms. If you do not
            agree, do not use the Service.
          </p>

          <section>
            <h2>1. Description of Service</h2>
            <p>
              quickLOG is a cloud-based production intelligence and traceability platform
              designed for food manufacturing operations. It enables clients to record
              production logs, manage inventory, trace raw materials, monitor quality control
              points, and analyze operational data. The Service is provided on a
              software-as-a-service (SaaS) basis.
            </p>
          </section>

          <section>
            <h2>2. Account Registration & Access</h2>
            <ul>
              <li>
                Access to the Service is granted by PV Business Consulting, LLC on a
                per-organization basis. Each organization is assigned a dedicated tenant
                environment.
              </li>
              <li>
                Your organization's administrator is responsible for creating, managing,
                and deactivating user accounts within your tenant.
              </li>
              <li>
                You are responsible for maintaining the confidentiality of all login
                credentials and for all activity that occurs under your accounts.
              </li>
              <li>
                You must notify us immediately at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> if you become
                aware of any unauthorized access or security breach.
              </li>
              <li>
                Accounts may not be shared, transferred, or sublicensed to any third party
                without our prior written consent.
              </li>
            </ul>
          </section>

          <section>
            <h2>3. Acceptable Use</h2>
            <p>You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree <strong>not</strong> to:</p>
            <ul>
              <li>Use the Service for any unlawful purpose or in violation of any applicable regulations</li>
              <li>Attempt to gain unauthorized access to any part of the Service, its infrastructure, or other clients' data</li>
              <li>Reverse engineer, decompile, or attempt to extract source code from the Service</li>
              <li>Upload or transmit malicious code, viruses, or any software designed to harm the Service or its users</li>
              <li>Scrape, crawl, or systematically extract data from the Service using automated means</li>
              <li>Misrepresent the origin or accuracy of data entered into the platform</li>
              <li>Attempt to circumvent multi-tenant data isolation or access another organization's data</li>
              <li>Use the Service in any manner that could impair, overload, or damage the platform's infrastructure</li>
            </ul>
          </section>

          <section>
            <h2>4. Client Data & Ownership</h2>
            <ul>
              <li>
                <strong>Your data is yours.</strong> All production records, inventory data,
                and other content entered by your organization remains the exclusive property
                of your organization ("Client Data").
              </li>
              <li>
                You grant us a limited license to store, process, and display Client Data
                solely to provide and improve the Service.
              </li>
              <li>
                We will not access, use, or disclose your Client Data except as necessary
                to operate the Service, comply with legal obligations, or with your explicit
                permission.
              </li>
              <li>
                You are solely responsible for the accuracy, quality, and legality of Client
                Data you enter into the platform. quickLOG does not verify the accuracy of
                production or compliance data.
              </li>
            </ul>
          </section>

          <section>
            <h2>5. Intellectual Property</h2>
            <ul>
              <li>
                The quickLOG platform, including its software, design, trademarks, and
                documentation, is the exclusive intellectual property of
                PV Business Consulting, LLC. All rights reserved.
              </li>
              <li>
                These Terms do not grant you any ownership rights in the Service. You receive
                only a limited, non-exclusive, non-transferable right to access the Service
                as described herein.
              </li>
              <li>
                Feedback, suggestions, or feature requests you provide may be used by us to
                improve the Service without any obligation or compensation to you.
              </li>
            </ul>
          </section>

          <section>
            <h2>6. Service Availability & Support</h2>
            <ul>
              <li>
                We strive to maintain high availability of the Service but do not guarantee
                uninterrupted access. Scheduled maintenance and unexpected outages may occur.
              </li>
              <li>
                We reserve the right to modify, suspend, or discontinue any feature of the
                Service at any time with reasonable notice.
              </li>
              <li>
                Support is provided via email at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Response times are
                made on a best-effort basis.
              </li>
            </ul>
          </section>

          <section>
            <h2>7. Fees & Payment</h2>
            <p>
              Pricing and payment terms are set forth in a separate order form or agreement
              between your organization and PV Business Consulting, LLC. Failure to pay
              applicable fees may result in suspension or termination of access to the Service.
            </p>
          </section>

          <section>
            <h2>8. Confidentiality</h2>
            <p>
              Each party agrees to keep confidential any non-public information received from
              the other party that is designated as confidential or that reasonably should be
              understood to be confidential given the nature of the information. This obligation
              survives termination of these Terms for a period of three (3) years.
            </p>
          </section>

          <section>
            <h2>9. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY
              KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>
            <p>
              WE DO NOT WARRANT THAT THE SERVICE WILL BE ERROR-FREE, UNINTERRUPTED, OR FREE
              FROM SECURITY VULNERABILITIES. USE OF THE SERVICE IS AT YOUR SOLE RISK.
            </p>
            <p>
              quickLOG is a record-keeping and traceability tool. It does not substitute for
              professional food safety advice, regulatory compliance counsel, or certified
              HACCP/food safety systems. Your organization remains solely responsible for
              regulatory compliance.
            </p>
          </section>

          <section>
            <h2>10. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL
              PV BUSINESS CONSULTING, LLC BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, LOSS OF DATA,
              BUSINESS INTERRUPTION, OR REGULATORY FINES, ARISING OUT OF OR RELATED TO
              YOUR USE OF OR INABILITY TO USE THE SERVICE, EVEN IF WE HAVE BEEN ADVISED OF
              THE POSSIBILITY OF SUCH DAMAGES.
            </p>
            <p>
              OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING UNDER THESE TERMS SHALL NOT
              EXCEED THE AMOUNTS PAID BY YOU FOR THE SERVICE IN THE TWELVE (12) MONTHS
              PRECEDING THE CLAIM.
            </p>
          </section>

          <section>
            <h2>11. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless PV Business Consulting, LLC and its
              officers, employees, and agents from any claims, damages, losses, or expenses
              (including reasonable legal fees) arising out of: (a) your use of the Service
              in violation of these Terms; (b) Client Data you submit to the platform;
              or (c) your violation of any applicable law or regulation.
            </p>
          </section>

          <section>
            <h2>12. Term & Termination</h2>
            <ul>
              <li>
                These Terms remain in effect for as long as your organization uses the Service.
              </li>
              <li>
                Either party may terminate the relationship with reasonable written notice.
              </li>
              <li>
                We may suspend or terminate access immediately if you materially breach these
                Terms, including non-payment, misuse, or security incidents attributable to
                your organization.
              </li>
              <li>
                Upon termination, your access to the Service will be revoked. You may request
                a data export within 30 days of termination notice. After 90 days, Client Data
                will be permanently deleted per our retention policy.
              </li>
            </ul>
          </section>

          <section>
            <h2>13. Modifications to Terms</h2>
            <p>
              We reserve the right to update these Terms at any time. We will provide at least
              14 days' notice of material changes by email to your organization's registered
              administrator. Continued use of the Service after the effective date constitutes
              acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2>14. Governing Law & Disputes</h2>
            <p>
              These Terms are governed by the laws of the United States and the State of
              Florida, without regard to conflict of law principles. Any dispute arising from
              these Terms shall first be attempted to be resolved through good-faith
              negotiation. If unresolved, disputes shall be submitted to binding arbitration
              in accordance with the rules of the American Arbitration Association, unless
              either party elects to pursue claims in small claims court.
            </p>
          </section>

          <section>
            <h2>15. Entire Agreement</h2>
            <p>
              These Terms, together with any separate order form or service agreement, constitute
              the entire agreement between you and PV Business Consulting, LLC regarding the
              Service and supersede all prior agreements or understandings. If any provision of
              these Terms is found unenforceable, the remaining provisions will continue in
              full force.
            </p>
          </section>

          <section>
            <h2>16. Contact</h2>
            <p>
              For questions about these Terms, contact us at:<br />
              <strong>PV Business Consulting, LLC</strong><br />
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </p>
          </section>

        </div>

        <div className="policy-footer">
          <p>&copy; {new Date().getFullYear()} quickLOG by PV Business Consulting, LLC. All rights reserved.</p>
          <div className="policy-footer-links">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/">Back to Login</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsOfServicePage;
